import type { TravelDetailsFormValues } from "@/D2C/features/travel-quote/redux/type";
import { buildTravelIssuePolicyPayload } from "@/D2C/features/travel-quote/utils/travelQuotePayloadsUtils";
import { App_Url_New, LOB_IDS } from "@/shared/common/static";
import { axiosInstance } from "@/shared/lib/axiosInstance";

const DEFAULT_PAYMENT_FAILURE_MESSAGE =
  "Payment failed. Please check your card details and try again.";

function normalizePaymentStatus(paymentResponse: any): string {
  const directStatus =
    paymentResponse?.payment_status ?? paymentResponse?.paymentStatus ?? paymentResponse?.status;
  const nestedStatus =
    paymentResponse?.data?.payment_status ??
    paymentResponse?.data?.paymentStatus ??
    paymentResponse?.data?.status;

  return String(directStatus ?? nestedStatus ?? "")
    .trim()
    .toLowerCase();
}

function getPaymentFailureReason(paymentResponse: any): string {
  return (
    paymentResponse?.failure_reason ||
    paymentResponse?.failureReason ||
    paymentResponse?.data?.failure_reason ||
    paymentResponse?.data?.failureReason ||
    DEFAULT_PAYMENT_FAILURE_MESSAGE
  );
}

function getApiErrorMessage(error: any, fallback: string): string {
  return (
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  );
}

export async function createTravelSession(payload: Record<string, unknown>) {
  const response = await axiosInstance.post(App_Url_New.ApiEndPoint.createSession, payload);

  return response.data;
}

export async function updateTravelSessionStep(params: {
  step: string;
  payload: Record<string, unknown>;
  clientId: string | number;
  sessionId: string | number;
  lobId: number;
  start_date?: string | null;
  end_date?: string | null;
}) {
  const { step, payload, clientId, sessionId, lobId, start_date, end_date } = params;

  return axiosInstance.post(App_Url_New.ApiEndPoint.updateSession, {
    step,
    payload,
    client_id: Number(clientId),
    session_id: Number(sessionId),
    lob_id: lobId,
    ...(start_date !== undefined ? { start_date } : {}),
    ...(end_date !== undefined ? { end_date } : {}),
  });
}

export async function submitTravelPayment(payload: Record<string, unknown>) {
  const response = await axiosInstance.post(App_Url_New.ApiEndPoint.paymentGateway, payload);

  return response.data;
}

export async function issueTravelPolicy(payload: Record<string, unknown>) {
  const response = await axiosInstance.post(App_Url_New.ApiEndPoint.travelGateway, payload);

  return response.data;
}

export type TravelPaymentFlowResult =
  | {
      status: "SUCCESS";
      paymentResponse: any;
      issuePolicyResponse: any;
      policyNumber: string;
      quoteNo: string;
      apiStatus: string;
    }
  | {
      status: "FAILED";
      paymentResponse?: any;
      failureReason: string;
    }
  | {
      status: "SUCCESS_POLICY_FAILED";
      paymentResponse: any;
      failureReason: string;
    };

export async function runTravelPaymentFlow(params: {
  selectedQuote: any;
  clientId: string | number | null;
  sessionId: string | number | null;
  paymentPayload: Record<string, unknown>;
  travelDetails: Pick<TravelDetailsFormValues, "startDate" | "endDate">;
}): Promise<TravelPaymentFlowResult> {
  const { selectedQuote, clientId, sessionId, paymentPayload, travelDetails } = params;

  try {
    const paymentResponse = await submitTravelPayment(paymentPayload);

    const normalizedPaymentStatus = normalizePaymentStatus(paymentResponse);

    if (normalizedPaymentStatus !== "success") {
      return {
        status: "FAILED",
        paymentResponse,
        failureReason: getPaymentFailureReason(paymentResponse),
      };
    }

    const issuePolicyPayload = buildTravelIssuePolicyPayload({
      selectedQuote,
      clientId,
      sessionId,
      paymentResponse,
      travelDetails,
      lobId: LOB_IDS.TRAVEL,
    });

    try {
      const issuePolicyResponse = await issueTravelPolicy(issuePolicyPayload);
      const apiResponse = issuePolicyResponse?.response;

      return {
        status: "SUCCESS",
        paymentResponse,
        issuePolicyResponse,
        policyNumber: apiResponse?.policyNo || issuePolicyResponse?.policyNo || "",
        quoteNo: issuePolicyResponse?.quoteNo || "",
        apiStatus:
          apiResponse?.status || issuePolicyResponse?.status || "Policy Issued Successfully",
      };
    } catch (issueError: any) {
      return {
        status: "SUCCESS_POLICY_FAILED",
        paymentResponse,
        failureReason: getApiErrorMessage(
          issueError,
          "Payment was successful, but policy generation failed. Please contact support."
        ),
      };
    }
  } catch (paymentError: any) {
    return {
      status: "FAILED",
      failureReason: getApiErrorMessage(paymentError, DEFAULT_PAYMENT_FAILURE_MESSAGE),
    };
  }
}
